import { useState } from 'react';
import AnimatedSection from '../../../components/base/AnimatedSection';

const services = [
  {
    id: 'recording',
    icon: 'ri-mic-2-line',
    label: 'MOST POPULAR',
    title: 'Recording Session',
    tagline: 'Lay down your best performance',
    price: '$60',
    unit: '/ hr',
    minimum: '2 hr minimum',
    bookingFee: '$35 booking fee required',
    color: 'from-red-500 to-rose-700',
    accentColor: 'text-red-500',
    borderColor: 'border-red-500',
    includes: [
      'Professional vocal booth & mic setup',
      'Real-time engineer monitoring & direction',
      'Punch-in recording & comping',
      'Headphone mix customized for you',
      'Raw session files delivered (WAV)',
      'Up to 2 revisions on mix-down',
      'Minimum 2-hour session required',
    ],
    note: 'Perfect for singles, EPs, albums, and voice-over work.',
  },
  {
    id: 'mix-mastering',
    icon: 'ri-equalizer-line',
    label: 'BEST VALUE',
    title: 'Mix & Mastering',
    tagline: 'Polish your sound to perfection',
    price: '$100',
    unit: '/song',
    minimum: null,
    bookingFee: '$35 booking fee required',
    color: 'from-red-800 to-red-950',
    accentColor: 'text-red-400',
    borderColor: 'border-red-400',
    includes: [
      'Full multi-track mix from your stems',
      'EQ, compression & dynamic processing',
      'Reverb, delay & spatial effects',
      'Stereo widening & frequency balancing',
      'Loudness-optimized master (streaming ready)',
      'Delivered in WAV format (MP3 available on request via text)',
      'Up to 3 revision rounds included',
    ],
    note: 'Ideal for artists who already have recorded tracks and need a radio-ready finish.',
  },
  {
    id: 'exclusive',
    icon: 'ri-vip-crown-line',
    label: 'PREMIUM',
    title: 'Exclusive Project',
    tagline: 'Full creative partnership',
    price: 'Custom',
    unit: '',
    minimum: null,
    bookingFee: 'No booking fee',
    color: 'from-red-400 to-red-600',
    accentColor: 'text-red-400',
    borderColor: 'border-red-400',
    includes: [
      'JayMaximus works directly with you',
      'Unlimited studio time for the project scope',
      'Custom beat production from scratch',
      'Full recording, mixing & mastering included',
      'Exclusive rights — no resale, ever',
      'Priority scheduling & fast turnaround',
      'Direct line to producer for feedback',
    ],
    note: 'Built for artists serious about owning their sound end-to-end. Pricing discussed on consultation.',
  },
];

export default function ServicesSection() {
  const [activeId, setActiveId] = useState<string>('recording');

  return (
    <section id="services" className="py-16 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-6">

        {/* Header */}
        <AnimatedSection animation="fade-up" className="text-center mb-10 md:mb-16">
          <div className="inline-block px-4 py-2 border border-gray-300 rounded-full text-xs font-semibold text-gray-700 mb-3 md:mb-4 tracking-widest">
            SERVICES & PRICING
          </div>
          <h2 className="text-3xl md:text-6xl font-bold text-gray-900 mb-3 md:mb-4 leading-tight">
            What&apos;s Included in<br />Each Session
          </h2>
          <p className="text-sm md:text-lg text-gray-500 max-w-xl mx-auto">
            Transparent pricing. No hidden fees. Every session is engineered to get the best out of your music.
          </p>
        </AnimatedSection>

        {/* Tab Switcher */}
        <AnimatedSection animation="fade-up" delay={200} className="flex justify-center mb-8 md:mb-12">
          <div className="overflow-x-auto no-scrollbar max-w-full">
            <div className="inline-flex bg-gray-100 rounded-full px-1 py-1 gap-1 whitespace-nowrap">
              {services.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setActiveId(s.id)}
                  className={`px-4 md:px-6 py-2 rounded-full text-xs md:text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    activeId === s.id
                      ? 'bg-gray-900 text-white shadow-md'
                      : 'text-gray-500 hover:text-gray-800'
                  }`}
                >
                  {s.title}
                </button>
              ))}
            </div>
          </div>
        </AnimatedSection>

        {/* Cards Grid */}
        <AnimatedSection animation="fade-up" delay={350} className="grid md:grid-cols-3 gap-4 md:gap-6 items-stretch">
          {services.map((service) => {
            const isActive = activeId === service.id;
            return (
              <div
                key={service.id}
                onClick={() => setActiveId(service.id)}
                className={`relative rounded-3xl border-2 transition-all duration-300 cursor-pointer overflow-hidden flex flex-col ${
                  isActive
                    ? `${service.borderColor} shadow-2xl scale-[1.01] md:scale-[1.02]`
                    : 'border-gray-200 hover:border-gray-300 hover:shadow-lg'
                }`}
              >
                {/* Gradient top bar */}
                <div className={`h-1.5 w-full bg-gradient-to-r ${service.color}`} />

                <div className="p-5 md:p-8 flex flex-col flex-1">
                  {/* Label badge */}
                  <div className="flex items-center justify-between mb-4 md:mb-6">
                    <span className={`text-xs font-bold tracking-widest ${service.id === 'exclusive' ? 'text-red-400' : 'text-red-500'}`}>
                      {service.label}
                    </span>
                    <div className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-gray-100">
                      <i className={`${service.icon} text-lg md:text-xl text-gray-700`}></i>
                    </div>
                  </div>

                  {/* Title & tagline */}
                  <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-1">{service.title}</h3>
                  <p className="text-xs md:text-sm text-gray-500 mb-4 md:mb-6">{service.tagline}</p>

                  {/* Price */}
                  <div className="flex items-end gap-1 mb-1">
                    <span className="text-4xl md:text-5xl font-extrabold text-gray-900 leading-none">{service.price}</span>
                    {service.unit && (
                      <span className="text-gray-400 text-base md:text-lg mb-1">{service.unit}</span>
                    )}
                  </div>
                  <div className="flex flex-col gap-0.5 mb-4 md:mb-6">
                    {service.minimum && (
                      <span className="text-xs text-gray-400 font-medium">{service.minimum}</span>
                    )}
                    <span className={`text-xs font-semibold ${service.id === 'exclusive' ? 'text-amber-500' : 'text-rose-500'}`}>
                      {service.bookingFee}
                    </span>
                  </div>

                  {/* Divider */}
                  <div className="border-t border-gray-100 mb-4 md:mb-6" />

                  {/* Includes list */}
                  <ul className="space-y-2 md:space-y-3 flex-1">
                    {service.includes.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 md:gap-3">
                        <div className="w-4 h-4 md:w-5 md:h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className={`ri-check-line text-sm font-bold ${service.accentColor}`}></i>
                        </div>
                        <span className="text-xs md:text-sm text-gray-600 leading-snug">{item}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Note */}
                  <p className="mt-4 md:mt-6 text-xs text-gray-400 italic leading-relaxed">{service.note}</p>

                  {/* CTA */}
                  <a
                    href="#booking"
                    onClick={(e) => e.stopPropagation()}
                    className={`mt-4 md:mt-6 block text-center py-3 md:py-3.5 rounded-xl font-semibold text-sm transition-all duration-300 whitespace-nowrap cursor-pointer ${
                      isActive
                        ? `bg-gradient-to-r ${service.color} text-white shadow-lg hover:opacity-90`
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Book This Session
                  </a>
                </div>
              </div>
            );
          })}
        </AnimatedSection>

        {/* Bottom disclaimer */}
        <AnimatedSection animation="fade-in" delay={500} className="mt-12 text-center">
          <p className="text-sm text-gray-400">
            <i className="ri-information-line mr-1"></i>
            All sessions require a <strong className="text-gray-600">$35 non-refundable booking fee</strong> (except Exclusive Project).
            Missing your appointment forfeits the fee. Contact us to reschedule at least 24 hrs in advance.
          </p>
          <p className="text-sm text-gray-400 mt-2">
            <i className="ri-music-line mr-1"></i>
            WAV files delivered upon completion.{' '}
            <a href="sms:+15716995343" className="underline text-gray-500 hover:text-gray-700 transition-colors whitespace-nowrap">Text +1 (571)-699-5343</a>
            {' '}to request an MP3 version — delivered as soon as possible.
          </p>
        </AnimatedSection>

      </div>
    </section>
  );
}
